



















































































































































import { Component, Watch, Mixins } from 'vue-property-decorator';
import { IMAGES_MIME, MAX_FILE_SIZE } from '@common/app.config';
import { MESSAGE_TYPE } from '@common/enums/message';
import Toast from '@common/helpers/toast';
import LocalStorageUtils from '@common/LocalStorageUtils';
import ApiHelper from 'api-helper';
import { State, Action } from 'vuex-class';
import { UserTreatment } from '@/models/user-treatment';
import { MessageEvent } from '@/events/message';
import WindowSizeMixin from '@common/mixins/WindowSizeMixin';
import { MessageTemplateModule } from '@/store';
import { Patient } from '@/models/patient';
import { getPatientFullName } from '@common/helpers/patient-info';
import { canUseMessageTemplate } from '@common/helpers/message-template-permission';
import PopupDelete from '@/components/common/PopupDelete.vue';

const MAX_LENGTH_MESSAGE = 3000;

@Component({
  components: {
    PopupDelete,
  },
})
export default class MessageInputModal extends Mixins(WindowSizeMixin) {
  @State((state) => state.auth.currentUser) currentUser!: any;
  @State((state) => state.treatment_detail.data)
  activeTreatment!: UserTreatment;
  @State((state) => state.draft_message.message)
  draftMessage!: string;
  @State((state) => state.patient_detail.data) patient!: Patient;
  @Action('draft_message/updateDraft') updateDraft!: (msg: string) => void;
  @Action('draft_message/resetDraft') resetDraft!: () => void;

  currentChannel!: any;
  messageChange!: any;
  message = '';
  isShiftEnter = LocalStorageUtils.getItem('isShiftEnter') || true;
  isSending = false;
  accept = IMAGES_MIME.join();
  isSendError: boolean = false;
  private dragCleanup: (() => void) | null = null;
  private isTemplateListOpen: boolean = false;
  private popoverStyle: Record<string, string> = {};
  private pendingTemplate: { content: string } | null = null;

  get fileInput() {
    return this.$refs.fileInput as HTMLInputElement;
  }

  get templateList() {
    return MessageTemplateModule.activeChatList;
  }

  get isTemplateListLoading(): boolean {
    return MessageTemplateModule.isChatListLoading;
  }

  // No.176: view restricted to super_admin/doctor — clinic_admin/cs/pharmacist
  // get a 400 from the API, so the button and its fetches must be gated here too.
  get canUseTemplate(): boolean {
    return canUseMessageTemplate(this.currentUser);
  }

  get patientName(): string {
    return getPatientFullName(this.patient);
  }

  get modalWidth() {
    return this.windowWidth <= 767 ? '100%' : 600;
  }

  get maxLengthMessage() {
    return MAX_LENGTH_MESSAGE;
  }

  get normalizedMessageLength(): number {
    return Array.from(this.normalizeMessage(this.message)).length;
  }

  normalizeMessage(text: string): string {
    return text
      .replace(/\r\n/g, '\n')
      .normalize('NFC');
  }

  created() {
    if (this.canUseTemplate && !MessageTemplateModule.chatList.length) {
      MessageTemplateModule.fetchChatList();
    }

    MessageEvent.$on('edit-message', (payload) => {
      this.messageChange = payload?.message ? { ...payload.message } : null;

      if (this.messageChange) {
        this.message = this.messageChange.message_content;
        this.$nextTick(() => {
          this.autoResize();
          document.getElementById('inputMessage')?.focus();
        });
      }
    });

    MessageEvent.$on('reset-edit', () => {
      this.resetMessage();
    });
  }

  mounted() {
    this.autoResize();
    document.addEventListener('click', this.handleOutsideClick);
  }

  beforeDestroy() {
    MessageEvent.$off('edit-message');
    MessageEvent.$off('reset-edit');
    document.removeEventListener('click', this.handleOutsideClick);
    this.removePopoverPositionListeners();
    this.dragCleanup?.();
    this.dragCleanup = null;
  }

  open() {
    this.$modal.show('message-input-modal');
  }

  close() {
    this.$modal.hide('message-input-modal');
  }

  handleBeforeClose() {
    if (this.messageChange) {
      this.resetDraft();
    }
    this.messageChange = null;
    this.dragCleanup?.();
    this.dragCleanup = null;
  }

  onInput() {
    const normalized = this.normalizeMessage(this.message);

    if (normalized !== this.message) {
      this.message = normalized;
    }

    this.$nextTick(() => {
      this.autoResize();
    });
  }

  autoResize() {
    const textarea = this.$refs.messageInput as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }

    const scrollHeight = textarea.scrollHeight;
    if (scrollHeight > 200) {
      textarea.classList.add('scrollable');
    } else {
      textarea.classList.remove('scrollable');
    }

    textarea.style.height = 'auto';
    const lineHeight = 20;
    const minHeight = lineHeight * 3;

    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight));
    textarea.style.height = `${newHeight}px`;
  }

  @Watch('message')
  onMessageChange(newVal: string) {
    this.updateDraft(newVal);
  }

  @Watch('activeTreatment')
  onActiveTreatmentSelectionChange() {
    this.resetDraft();
    this.message = '';
    this.$nextTick(() => {
      this.autoResize();
    });
  }

  resetMessage() {
    if (!this.isSendError) {
      this.message = '';
      this.messageChange = null;
    }

    this.isSendError = false;
    this.isSending = false;
    (this.$refs.fileInput as HTMLInputElement).value = '';
    this.$nextTick(() => {
      this.autoResize();
    });
    return;
  }

  sendMessageWhenKeyPress(event: KeyboardEvent) {
    // Ignore keydown events fired during IME composition (e.g. Japanese IME).
    // Without this guard, pressing Enter to confirm IME composition fires the
    // handler twice: once with isComposing=true (before commit) and once with
    // isComposing=false (the real Enter), resulting in two messages being sent.
    if (event.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.shiftKey) {
      if (!this.isShiftEnter) {
        return;
      }
      this.sendTextMessage(event);
      return;
    }
    if (this.isShiftEnter) {
      return;
    }
    this.sendTextMessage(event);
  }

  sendTextMessage(event: Event) {
    event.preventDefault();
    if (!this.message) {
      return;
    }

    const params = {
      channelId: this.currentChannel.id,
      message: this.message,
      image: null,
      type: MESSAGE_TYPE.TEXT,
    };
    this.sendMessageToUser(params);
  }

  onPaste(event: ClipboardEvent) {
    if (event.clipboardData) {
      const content = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, content);
    }
    if (this.message.length > MAX_LENGTH_MESSAGE) {
      this.message = this.message.substr(0, MAX_LENGTH_MESSAGE);
    }
  }

  checkMessageLength(event: KeyboardEvent) {
    const length = this.normalizedMessageLength;

    if (
      length >= MAX_LENGTH_MESSAGE &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ];

      if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
      }
    }
  }

  async sendAttachment(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files?.length || this.isSending) {
      return;
    }

    const file = files[0];
    if (file.size > MAX_FILE_SIZE) {
      Toast.error('サイズが5MBを超える画像はアップロードできません');
      return;
    }
    if (!IMAGES_MIME.includes(file.type)) {
      Toast.error('jpg又はpng形式でアップロードしてください');
      return;
    }

    const params = {
      channelId: this.currentChannel.id,
      message: null,
      image: file,
      type: MESSAGE_TYPE.IMAGE,
    };
    this.sendMessageToUser(params);
  }

  async sendMessageToUser({ channelId, message, image, type }) {
    try {
      this.isSending = true;

      if (!this.messageChange) {
        await ApiHelper.getApi('MessageApi').sendMessage(channelId, {
          message,
          image,
          type,
          user_treatment_id: this.activeTreatment.id,
        });
      } else {
        await ApiHelper.getApi('MessageApi').updateMessage(
          this.messageChange.id,
          {
            message,
            image,
            type,
            user_treatment_id: this.activeTreatment.id,
            _method: 'PUT',
          },
        );
      }
    } catch (error) {
      const msg =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'エラーが発生しました。もう一度お試しください。';

      Toast.error(msg);
      this.isSendError = true;
    } finally {
      this.isSending = false;
      this.resetMessage();
    }
  }

  updateIsShiftEnter() {
    LocalStorageUtils.saveItem('isShiftEnter', this.isShiftEnter);
  }

  toggleTemplateList(event: Event) {
    event.stopPropagation();
    this.isTemplateListOpen = !this.isTemplateListOpen;
    if (this.isTemplateListOpen) {
      this.$nextTick(() => this.updateTemplatePopoverPosition());
      this.addPopoverPositionListeners();
    } else {
      this.removePopoverPositionListeners();
    }
  }

  handleOutsideClick() {
    if (this.isTemplateListOpen) {
      this.isTemplateListOpen = false;
      this.removePopoverPositionListeners();
    }
  }

  addPopoverPositionListeners() {
    window.addEventListener('resize', this.updateTemplatePopoverPosition);
    // capture: true so this fires for scrolling inside the modal body too —
    // the modal's own scroll container (e.g. .v--modal-box on mobile) doesn't
    // bubble its scroll event to window otherwise, and without this the
    // popover would stay put while the テンプレート button scrolls away
    // under it.
    window.addEventListener('scroll', this.updateTemplatePopoverPosition, true);
  }

  removePopoverPositionListeners() {
    window.removeEventListener('resize', this.updateTemplatePopoverPosition);
    window.removeEventListener('scroll', this.updateTemplatePopoverPosition, true);
  }

  updateTemplatePopoverPosition() {
    const anchor = this.$refs.templateButton as HTMLElement;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const maxHeight = 320;
    const popoverWidth = Math.min(320, window.innerWidth - margin * 2);
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;

    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - popoverWidth - margin);
    }

    // Anchor with `bottom` (not `top`) when opening upward, so the popover's
    // own height (which can be much less than `maxHeight`) never pushes it
    // away from the button — and cap maxHeight to the space actually
    // available in whichever direction we open, so it can never spill past
    // the opposite edge of the viewport.
    if (spaceBelow >= spaceAbove) {
      this.popoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + 6}px`,
        left: `${left}px`,
        width: `${popoverWidth}px`,
        maxHeight: `${Math.max(0, Math.min(maxHeight, spaceBelow))}px`,
      };
    } else {
      this.popoverStyle = {
        position: 'fixed',
        bottom: `${window.innerHeight - rect.top + 6}px`,
        left: `${left}px`,
        width: `${popoverWidth}px`,
        maxHeight: `${Math.max(0, Math.min(maxHeight, spaceAbove))}px`,
      };
    }
  }

  applyTemplate(template: { content: string }) {
    this.isTemplateListOpen = false;
    this.removePopoverPositionListeners();

    if (this.message && this.message.trim().length) {
      this.pendingTemplate = template;
      this.$modal.show('template-overwrite-confirm-modal');
      return;
    }

    this.doApplyTemplate(template);
  }

  doApplyTemplate(template: { content: string }) {
    const patientName = this.patientName;
    const hasPlaceholder = /{{\s*patient_name\s*}}/.test(template.content);
    if (hasPlaceholder && !patientName) {
      Toast.error(
        '患者名を取得できなかったため、{{patient_name}}は置き換えられていません。送信前に手動で修正してください。',
      );
    }
    const replaced = patientName
      ? template.content.replace(/{{\s*patient_name\s*}}/g, patientName)
      : template.content;
    const normalized = this.normalizeMessage(replaced);
    this.message = Array.from(normalized).slice(0, MAX_LENGTH_MESSAGE).join('');
    this.$nextTick(() => {
      this.autoResize();
      document.getElementById('inputMessage')?.focus();
    });
  }

  applyPendingTemplate() {
    this.$modal.hide('template-overwrite-confirm-modal');
    if (this.pendingTemplate) {
      this.doApplyTemplate(this.pendingTemplate);
    }
    this.pendingTemplate = null;
  }

  cancelApplyTemplate() {
    this.$modal.hide('template-overwrite-confirm-modal');
    this.pendingTemplate = null;
  }

  onOpened() {
    this.$nextTick(() => {
      this.autoResize();
      this.enableDragging();
    });
  }

  enableDragging() {
    const modalRef = this.$refs.messageInputModal as any;
    const modal = modalRef.$el.querySelector('.v--modal-box') as HTMLElement;
    const header = this.$refs.dragHeader as HTMLElement;
    if (!modal || !header) {
      return;
    }

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    header.style.cursor = 'move';

    const startDrag = (clientX: number, clientY: number) => {
      isDragging = true;
      startX = clientX;
      startY = clientY;
      const rect = modal.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      modal.style.position = 'fixed';
      modal.style.margin = '0';
      modal.style.transform = 'none';
    };

    const moveDrag = (clientX: number, clientY: number) => {
      if (!isDragging) {
        return;
      }
      const dx = clientX - startX;
      const dy = clientY - startY;
      const newLeft = Math.min(
        Math.max(0, initialLeft + dx),
        window.innerWidth - modal.offsetWidth,
      );
      const newTop = Math.min(
        Math.max(0, initialTop + dy),
        window.innerHeight - modal.offsetHeight,
      );
      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
    };

    const endDrag = () => { isDragging = false; };

    // Mouse events (desktop)
    const onMouseDown = (e: MouseEvent) => {
      startDrag(e.clientX, e.clientY);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    const onMouseMove = (e: MouseEvent) => moveDrag(e.clientX, e.clientY);
    const onMouseUp = () => {
      endDrag();
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    // Touch events (mobile)
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      startDrag(t.clientX, t.clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      moveDrag(t.clientX, t.clientY);
    };
    const onTouchEnd = () => endDrag();

    header.addEventListener('mousedown', onMouseDown);
    header.addEventListener('touchstart', onTouchStart, { passive: true });
    header.addEventListener('touchmove', onTouchMove, { passive: false });
    header.addEventListener('touchend', onTouchEnd);

    this.dragCleanup?.();
    this.dragCleanup = () => {
      header.removeEventListener('mousedown', onMouseDown);
      header.removeEventListener('touchstart', onTouchStart);
      header.removeEventListener('touchmove', onTouchMove);
      header.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }

  handleBeforeOpen(event) {
    this.currentChannel = event.params.currentChannel;
    if (this.canUseTemplate) {
      MessageTemplateModule.fetchChatList();
    }

    if (this.messageChange) {
      this.message = this.messageChange.message_content;
    } else if (this.draftMessage) {
      this.message = this.draftMessage;
    } else {
      this.message = '';
    }

    this.$nextTick(() => this.autoResize());
  }
}
